import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import logoText from '../../assets/images/logo_text.png';
import UserAccountButton from '../auth/UserAccountButton';
import LocationDisplay from './LocationDisplay';
import { useUser } from '../../context/UserContext';
import { getCartItemCount } from '../../utils/cartService';
import analyticsService from '../../utils/analyticsService';
import { SearchIcon } from './PetIcons';

const Navbar = () => {
  const navigate = useNavigate();
  const { currentUser } = useUser();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [cartItemCount, setCartItemCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  // Define active and inactive class styles
  const activeClass = "text-primary font-medium";
  const inactiveClass = "text-primary-dark hover:text-primary transition-colors duration-300";

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Get cart item count
  useEffect(() => {
    const updateCartCount = async () => {
      try {
        const userId = currentUser?.email || null;
        const count = await getCartItemCount(userId);
        setCartItemCount(count);
      } catch (error) {
        console.error('Error updating cart count:', error);
        setCartItemCount(0);
      }
    };

    // Initial count
    updateCartCount();

    // Listen for storage events to update cart count when it changes
    window.addEventListener('storage', updateCartCount);

    // Custom event for cart updates from within the same window
    window.addEventListener('cartUpdated', updateCartCount);

    return () => {
      window.removeEventListener('storage', updateCartCount);
      window.removeEventListener('cartUpdated', updateCartCount);
    };
  }, [currentUser]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const term = searchQuery.trim();
    if (term) {
      analyticsService.logSearch(term);
      setIsMobileMenuOpen(false);
      navigate(`/products?search=${encodeURIComponent(term)}`);
    }
  };

  const searchForm = (idSuffix) => (
    <form
      role="search"
      onSubmit={handleSearchSubmit}
      className="flex items-center w-full bg-gray-100 focus-within:bg-white border border-gray-200 focus-within:border-primary rounded-full transition-colors duration-200 overflow-hidden"
    >
      <label htmlFor={`nav-search-${idSuffix}`} className="sr-only">
        Search pet products
      </label>
      <input
        id={`nav-search-${idSuffix}`}
        type="search"
        placeholder="Search food, toys, medicines…"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="flex-1 min-w-0 bg-transparent pl-4 pr-2 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none"
      />
      <button
        type="submit"
        aria-label="Search products"
        className="bg-primary hover:bg-primary-dark text-white p-2 m-1 rounded-full transition-colors duration-200 flex-shrink-0"
      >
        <SearchIcon className="w-4 h-4" />
      </button>
    </form>
  );

  return (
    <>
      {/* Single Responsive Navbar */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 shadow-md' : 'bg-white/90'} backdrop-blur-sm`}>
        <div className="container mx-auto px-4 md:px-6 py-2 md:py-3">
          <div className="flex justify-between items-center min-h-[50px] md:min-h-[60px] gap-4">
            {/* Logo and Location */}
            <div className="flex items-center flex-shrink min-w-0 gap-2 md:gap-3">
              <Link to="/home" className="flex items-center flex-shrink-0" aria-label="Petzify home">
                <img src={logoText} alt="Petzify" className="h-8 md:h-11 w-auto" width="140" height="44" />
              </Link>
              <div className="min-w-0 border-l border-gray-200 pl-2 md:pl-3">
                <span className="hidden md:block text-[10px] uppercase tracking-wider text-gray-400 font-semibold leading-none mb-0.5">
                  Deliver to
                </span>
                <LocationDisplay />
              </div>
            </div>

            {/* Desktop Search */}
            <div className="hidden md:block flex-1 max-w-md">
              {searchForm('desktop')}
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-6 flex-shrink-0">
              <NavLink end to="/home" className={({isActive}) => isActive ? activeClass : inactiveClass}>
                Home
              </NavLink>

              <NavLink to="/services" className={({isActive}) => isActive ? activeClass : inactiveClass}>
                Services
              </NavLink>

              <NavLink to="/pawdoc-ai" className={({isActive}) => isActive ? activeClass : inactiveClass}>
                <div className="flex items-center space-x-1">
                  {/*<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">*/}
                  {/*  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />*/}
                  {/*</svg>*/}
                  <span>AI Vet</span>
                </div>
              </NavLink>

              <NavLink to="/products" className={({isActive}) => isActive ? activeClass : inactiveClass}>
                Products
              </NavLink>

              {/* Cart Icon */}
              <button
                onClick={() => navigate("/cart")}
                aria-label="View cart"
                className="relative p-1 text-primary-dark hover:text-primary"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                {cartItemCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-primary text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {cartItemCount}
                  </span>
                )}
              </button>

              {/* User Account Button */}
              <UserAccountButton />
            </div>

            {/* Mobile Menu Icons */}
            <div className="md:hidden flex items-center space-x-4 flex-shrink-0">
              {/* Cart Icon */}
              <button
                onClick={() => navigate("/cart")}
                aria-label="View cart"
                className="relative p-1 text-primary-dark hover:text-primary"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                {cartItemCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-primary text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {cartItemCount}
                  </span>
                )}
              </button>

              {/* Menu Toggle Button */}
              <button
                className="text-primary-dark hover:text-primary"
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                {isMobileMenuOpen ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Mobile Search - always visible below the main row */}
          <div className="md:hidden pt-2 pb-1">
            {searchForm('mobile')}
          </div>
        </div>

        {/* Mobile Menu Dropdown - anchored to the bottom of the navbar */}
        {isMobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 md:hidden bg-white shadow-lg border-t border-gray-100">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              <NavLink end to="/home"
                className={({isActive}) =>
                  `block px-3 py-2 rounded-md text-base font-medium ${isActive ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100'}`
                }
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Home
              </NavLink>
              <NavLink to="/services"
                className={({isActive}) =>
                  `block px-3 py-2 rounded-md text-base font-medium ${isActive ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100'}`
                }
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Services
              </NavLink>
              <NavLink to="/pawdoc-ai"
                className={({isActive}) =>
                  `block px-3 py-2 rounded-md text-base font-medium ${isActive ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100'}`
                }
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <div className="flex items-center space-x-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  <span>AI Vet Chat</span>
                </div>
              </NavLink>
              <NavLink to="/products"
                className={({isActive}) =>
                  `block px-3 py-2 rounded-md text-base font-medium ${isActive ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100'}`
                }
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Products
              </NavLink>

              {/* User Account Button Mobile */}
              <div className="px-3 py-2">
                <UserAccountButton />
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;
